/**
 * Meteors (see docs/meteor-shop-plan.md). Once the player has enough
 * achievements, meteors fall into the yard now and then; clicking one down to
 * zero health before its countdown runs out pays meteor shards, the shop's
 * currency. Harder kinds pay more. Numbers are a starting point for tuning.
 */

export interface MeteorKind {
  id: string;
  name: string;
  health: number;
  /** Seconds to break it once it has landed. */
  countdown: number;
  shards: number;
  /**
   * How many fall at once, picked uniformly in this range. The small kinds
   * arrive as a shower so a spawn of them is worth crossing the yard for;
   * the big ones come alone. Absent means one.
   */
  shower?: { min: number; max: number };
}

/** Achievements earned before meteors start falling and the shop opens. */
export const METEOR_UNLOCK_ACHIEVEMENTS = 30;

/** Seconds of visible play between meteor falls, uniform in this range. */
export const METEOR_SPAWN_MIN_SECONDS = 2 * 60;
export const METEOR_SPAWN_MAX_SECONDS = 5 * 60;

/** Damage a click does before any shop tool. */
export const BASE_CLICK_DAMAGE = 1;

/**
 * Shards are priced per *fall*, not per rock, so each kind is worth a little
 * more than the one below it however many of it land: a pebble shower pays
 * about 4, a stone shower about 5, an iron 6, a core 12, a nova 24. The big kinds came
 * down when the sky started serving them by the half — a core was priced as
 * a 5%-of-spawns jackpot, and at 50% that price runs away.
 */
export const METEOR_KINDS: readonly MeteorKind[] = [
  {
    id: 'pebble',
    name: 'Pebble',
    health: 15,
    countdown: 20,
    shards: 1,
    shower: { min: 3, max: 5 },
  },
  { id: 'stone', name: 'Stone', health: 40, countdown: 20, shards: 2, shower: { min: 2, max: 3 } },
  { id: 'iron', name: 'Iron', health: 90, countdown: 15, shards: 6 },
  { id: 'core', name: 'Core', health: 200, countdown: 12, shards: 12 },
  { id: 'nova', name: 'Nova', health: 420, countdown: 10, shards: 24 },
];

/**
 * The sky escalates with the player's pick. Each tier's weights apply once
 * that tool is the best one owned, so the kinds a player cannot break yet
 * stay rare and the ones they have outgrown thin out and stop falling. The
 * pebbles that are 45% of a bare-handed sky are gone entirely by the drill.
 */
export interface SkyTier {
  /** The tool that brings this sky, or null for bare hands. */
  tool: string | null;
  weights: Readonly<Record<string, number>>;
}

export const SKY_TIERS: readonly SkyTier[] = [
  { tool: null, weights: { pebble: 45, stone: 35, iron: 15, core: 5, nova: 0 } },
  { tool: 'tool:stone-hammer', weights: { pebble: 30, stone: 35, iron: 30, core: 5, nova: 0 } },
  { tool: 'tool:iron-pick', weights: { pebble: 15, stone: 30, iron: 45, core: 10, nova: 0 } },
  // The first novas fall here and cannot be broken yet: 420 health in ten
  // seconds is out of reach until the drill. They are a sight, not a reward.
  { tool: 'tool:steel-pick', weights: { pebble: 5, stone: 15, iron: 45, core: 30, nova: 5 } },
  { tool: 'tool:diamond-drill', weights: { pebble: 0, stone: 10, iron: 35, core: 40, nova: 15 } },
];

export const METEOR_KINDS_BY_ID: ReadonlyMap<string, MeteorKind> = new Map(
  METEOR_KINDS.map((k) => [k.id, k]),
);
